import {
    getCommentListUrl,
    getExcessCommentListUrl,
    getWorkCommentListUrl,
    saveNewPositionExtraWorkCommentUrl,
    saveNewPositionCommentUrl,
    saveNewPositionExcessCommentUrl,
    deleteExtraWorkCommentUrl,
    deleteExcessCommentListUrl,
    deleteCommentUrl,
} from '@entities/comment/api/routes';
import { submitAdapter } from '@entities/comment/lib';
import { TCommentApiResponse, TCommentType, TUploadData } from '@entities/comment/model/types';
import { $api } from '@shared/api';

const throwIfCommentApiError = <
    TResponse extends {
        data?: TCommentApiResponse & { status?: 'success' | 'error' | 'tariff-error' };
    },
>(
    response: TResponse
): TResponse => {
    if (response.data?.error || response.data?.status === 'error') {
        throw new Error(response.data.message || 'Failed to save comment');
    }

    return response;
};

export const commentApiHandlers = {
    getCommentList: async (
        { signal }: { signal: AbortSignal },
        {
            positionId,
            commentType,
        }: {
            positionId: string;
            commentType: TCommentType;
        }
    ) => {
        let formattedUrl;
        switch (commentType) {
            case 'positionComment':
                formattedUrl = getCommentListUrl(positionId);
                break;
            case 'excessComment':
                formattedUrl = getExcessCommentListUrl(positionId);
                break;
            case 'extraWorkComment':
                formattedUrl = getWorkCommentListUrl(positionId);
                break;
            default:
                formattedUrl = getCommentListUrl(positionId);
        }
        return throwIfCommentApiError(await $api(formattedUrl, { signal }));
    },
    deletePositionComment: async ({
        positionId,
        commentId,
        commentType,
    }: {
        positionId: string;
        commentId: string;
        commentType: TCommentType;
    }) => {
        let formattedUrl;
        switch (commentType) {
            case 'positionComment':
                formattedUrl = deleteCommentUrl({ guid: positionId, commentId });
                break;
            case 'excessComment':
                formattedUrl = deleteExcessCommentListUrl({ guid: positionId, commentId });
                break;
            case 'extraWorkComment':
                formattedUrl = deleteExtraWorkCommentUrl({ guid: positionId, commentId });
                break;
            default:
                formattedUrl = deleteCommentUrl({ guid: positionId, commentId });
        }

        return throwIfCommentApiError(await $api.post(formattedUrl));
    },
    saveNewPositionComment: async ({
        positionId,
        data,
        commentType,
    }: {
        positionId: string;
        data: TUploadData;
        commentType: TCommentType;
    }) => {
        let formattedUrl;
        switch (commentType) {
            case 'positionComment':
                formattedUrl = saveNewPositionCommentUrl(positionId);
                break;
            case 'excessComment':
                formattedUrl = saveNewPositionExcessCommentUrl(positionId);
                break;
            case 'extraWorkComment':
                formattedUrl = saveNewPositionExtraWorkCommentUrl(positionId);
                break;
            default:
                formattedUrl = saveNewPositionCommentUrl(positionId);
        }

        return throwIfCommentApiError(await $api.post(formattedUrl, submitAdapter(data)));
    },
};
